class ProductBuyBar extends HTMLElement {
  constructor() {
    "use strict";
    super();

    let classes = { show: "show" };
    let mainProductForm = document.querySelector(".product-form");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // In view -> Hide bar
            this.classList.remove(classes.show);
          } else if (window.scrollY > 200) {
            // Out of view AND scrolled a little from top -> Show bar
            this.classList.add(classes.show);
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(mainProductForm);

    // This script is loaded in product-template.liquid --> Footer observer after DOM ready
    document.addEventListener("DOMContentLoaded", function () {
      let siteFooter = document.querySelector("footer.footer");
      if (!siteFooter) return;
      observer.observe(siteFooter);
    });
  }
}
customElements.define("product-buy-bar", ProductBuyBar);

class UpsellCard extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.image = this.querySelector('.card-media img');
    this.priceDisplay = this.querySelector('.upsell-price-display');
    this.productId = parseInt(this.dataset.productId);

    if (this.select) {
      this.select.addEventListener('change', (event) => {
        const selectedOption = event.target.options[event.target.selectedIndex];
        const newImgSrc = selectedOption.getAttribute('data-variant-img-src');
        if (newImgSrc) { this.updateImage(newImgSrc) }
        
        const variantId = parseInt(event.target.value);
        
        // Update price display for pro customer discounts
        if (this.priceDisplay && window.variantMetafields && window.upsellProductData && this.productId) {
          this.updatePriceDisplay(this.productId, variantId);
        }
        
        // Check professional availability for selected variant
        if (window.variantMetafields && this.productId) {
          this.toggleProfessionalAvailability(this.productId, variantId);
        }
      });
      
      // Check initial variant on load
      if (this.select.value) {
        const variantId = parseInt(this.select.value);
        if (window.variantMetafields && this.productId) {
          this.toggleProfessionalAvailability(this.productId, variantId);
        }
      }
    }
  }

  updateImage(newImgSrc) {
    if (this.image && newImgSrc) {
      // Sanitize and validate URL to prevent XSS
      // Remove any whitespace and HTML entities
      const sanitized = String(newImgSrc).trim().replace(/[\s<>"']/g, '');

      // Reject dangerous protocols (check anywhere in string, not just start)
      const dangerousProtocols = /^(javascript|data|vbscript|file|about):/i;
      if (dangerousProtocols.test(sanitized)) {
        return; // Reject dangerous URLs
      }

      try {
        // Try to parse as absolute URL
        const url = new URL(sanitized, window.location.origin);
        // Only allow http and https protocols
        if (url.protocol === 'http:' || url.protocol === 'https:') {
          // Use the properly constructed URL to prevent any encoding issues
          this.image.src = url.href;
        }
      } catch (e) {
        // If URL parsing fails, it's likely a relative URL
        // Additional validation: ensure it doesn't contain dangerous patterns
        if (!sanitized.includes('javascript:') &&
            !sanitized.includes('data:') &&
            !sanitized.includes('vbscript:') &&
            !sanitized.includes('<') &&
            !sanitized.includes('>')) {
          // For relative URLs, ensure proper encoding
          this.image.src = sanitized;
        }
      }
    }
  }

  updatePriceDisplay(productId, variantId) {
    const variantData = window.variantMetafields[productId]?.[variantId];
    const productData = window.upsellProductData[productId];
    
    if (!variantData || !productData) return;
    
    const priceElement = this.priceDisplay.querySelector('.price__regular');
    if (!priceElement) return;
    
    let price = variantData.price;
    const compareAtPrice = variantData.compare_at_price;
    const professionalOnly = variantData.professional_only;
    
    // Check if there's a standard sale (compare_at_price > price)
    const hasStandardSale = compareAtPrice && compareAtPrice > price;
    
    // Calculate pro customer discount if applicable
    let showProDiscount = false;
    let originalPrice = price;
    let discountedPrice = price;
    
    if (window.proCustomer && !hasStandardSale) {
      const customerTags = window.customerTags || [];
      
      if (customerTags.includes('trained') && productData.trained_discount > 0) {
        showProDiscount = true;
        discountedPrice = Math.round(price * (100 - productData.trained_discount) / 100);
      } else if (customerTags.includes('company') && productData.company_discount > 0) {
        showProDiscount = true;
        discountedPrice = Math.round(price * (100 - productData.company_discount) / 100);
      }
    }
    
    // Format money using Woolman utility
    const formatMoney = (cents) => {
      if (typeof Woolman !== 'undefined' && Woolman.Utils?.formatMoney) {
        return Woolman.Utils.formatMoney(cents);
      }
      // Fallback formatting
      return `€${(cents / 100).toFixed(2)}`;
    };
    
    // Update display based on conditions
    if (professionalOnly && !window.proCustomer) {
      // Show "For professionals" text
      priceElement.innerHTML = '<span class="price-item price-item--regular price-item--pro-text">For professionals</span>';
    } else if (showProDiscount) {
      // Show strikethrough original + pink discounted price
      priceElement.innerHTML = `
        <div class="price__regular-pro" style="display: flex; gap: 10px;">
          <span class="price-item price-item--regular" style="text-decoration: line-through;">
            ${formatMoney(originalPrice)}
          </span>
          <span class="price-item price-item--regular" style="color: #FC16B0;">
            ${formatMoney(discountedPrice)}
          </span>
        </div>
      `;
    } else {
      // Show regular price
      priceElement.innerHTML = `<span class="price-item price-item--regular">${formatMoney(price)}</span>`;
    }
  }

  toggleProfessionalAvailability(productId, variantId) {
    // Check if any variant in this product has professional_only flag
    let hasProfessionalOnlyVariants = false;
    for (const [key, value] of Object.entries(window.variantMetafields[productId] || {})) {
      if (value.professional_only === true) {
        hasProfessionalOnlyVariants = true;
        break;
      }
    }
    if (!hasProfessionalOnlyVariants) return;

    const professionalOnly = window.variantMetafields[productId]?.[variantId]?.professional_only || false;
    const formId = this.getAttribute('data-form');
    const productForm = document.getElementById(formId);
    if (!productForm) return;
    
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = addButton?.querySelector('span');
    
    if (!addButton) return;

    if (professionalOnly) {
      if (window.proCustomer === true) {
        // Pro customer viewing restricted variant - allow purchase
        if (addButtonText) addButtonText.textContent = window.variantStrings.addToCart;
        addButton.disabled = false;
      } else {
        // Non-pro customer viewing restricted variant - block purchase
        if (addButtonText) addButtonText.textContent = window.variantStrings.only_for_pros;
        addButton.disabled = true;
      }
    } else {
      // Non-restricted variant - allow purchase for everyone
      if (addButtonText) addButtonText.textContent = window.variantStrings.addToCart;
      addButton.disabled = false;
    }
  }
}

customElements.define('upsell-card', UpsellCard);

// Hide .rte ul lists inside .product-block__text when every li has no visible text (e.g. empty rich text bullet points)
document.addEventListener("DOMContentLoaded", function () {
  document.querySelectorAll(".product-block__text .rte ul").forEach((list) => {
    const items = list.querySelectorAll("li");
    const hasContent = Array.from(items).some((item) => item.textContent.trim() !== "");
    if (!hasContent) list.classList.add("hide");
  });
});